import React, { Fragment, useEffect, useReducer, useRef } from "react";
import { Link } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useAuth0 } from "@auth0/auth0-react";
import DataTable from "react-data-table-component";

import axios from "axios";
import ai from "../../helpers/axios";
import columns from "./opi-columns";
import nonAdminColumns from "./opi-columns-non-admin";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Search from "../search";
import Loader from "../loader";

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        operators: {
          data: action.payload.data.operators,
        },
        loading: false,
        totalLength: action.payload.data.countTotal,
      };
    }
    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }
    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }
    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }
    default:
      break;
  }
  return state;
};

const restrictedColumn = {
  name: "Restrictions",
  selector: "restrictions",
  sortable: true,
  width: "200px",
  cell: (row) => {
    let restrictions = [];
    if (row?.restrictions?.length) {
      restrictions = row.restrictions.flatMap((item) => {
        let label = [];
        if (item === "caution") {
          label = "Use With Caution";
        }

        if (item === "blacklist") {
          label = "Blacklist";
        }

        if (item === "recovery") {
          label = "Recovery Only";
        }

        return label;
      });

      return restrictions.join(", ");
    }
  },
};

const notesColumn = {
  name: "Notes",
  selector: "notes",
  sortable: false,
  width: "200px",
};

const insertItem = (array, index, item) => {
  let newArray = [...array];

  newArray.splice(index, 0, item);

  return newArray;
};

const OPIContent = ({ isRestricted = false }) => {
  const initialState = {
    operators: {
      data: [],
    },
    loading: true,
    countPerPage: isRestricted ? 25 : 50,
    currentPage: 1,
    query: "",
    totalLength: null,
    sortBy: isRestricted ? "restrictions" : "composedScore",
    sortDirection: isRestricted ? "asc" : "desc",
  };

  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];

  const superAdminOrAD = userRoles.some(function (role) {
    return "Super Admin" === role || "Account Director" === role;
  });

  let revisedColumns = insertItem(columns, 2, restrictedColumn);
  let revisedNonAdminColumns = insertItem(nonAdminColumns, 2, restrictedColumn);

  revisedColumns = insertItem(revisedColumns, 4, notesColumn);
  revisedNonAdminColumns = insertItem(revisedNonAdminColumns, 4, notesColumn);

  const [state, dispatch] = useReducer(reducer, initialState);

  const abortControllerRef = useRef(null);

  const getData = async (query, loadSilently) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        return loginWithRedirect();
      });

      const { data } = await ai.auth(token).get(`/api/opi/list`, {
        signal,
        params: {
          query: query.trim(),
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
          isRestricted,
        },
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  };

  useEffect(() => {
    document.title = `${isRestricted ? "Restricted " : ""}Operators | evoJets HQ`;
  }, [isRestricted]);

  useEffect(() => {
    getData(state.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback(
    (query) => {
      getData(query);
    },
    500,
  );

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });

    debouncedGetData(query);
  };

  const handleSort = (column, sortDirection) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={isRestricted ? "Restricted Operators" : "Operator Performance Index"}
        pretitle="Settings"
        breadcrumbs={[
          isRestricted ?
            { link: "/restricted-operators", label: "Restricted Operators" } :
            { link: "/opi", label: "Operator Performance Index" },
        ]}
        rightColumn={
          <div
            id="operator-actions-row"
            className="header-actions-row base-header-actions-row has-delete-btn opi-header"
          >
            <Search
              placeholder="Search Operators"
              onSearch={handleSearch}
              query={state.query}
              className="opi-search"
            />

            {superAdminOrAD && (
              <Link to="/opi/new" className="evo-blue base-button">
                Add Operator
              </Link>
            )}
          </div>
        }
      />

      <div
        className={`flight-table-container server-pagination-table-container opi-table ${
          !superAdminOrAD && `opi-auto-width`
        }`}
      >
        <DataTable
          data={state.operators.data}
          columns={isRestricted ? (superAdminOrAD ? revisedColumns : revisedNonAdminColumns) : (superAdminOrAD ? columns : nonAdminColumns)}
          noHeader
          highlightOnHover
          pagination
          paginationServer
          paginationPerPage={state.countPerPage}
          paginationTotalRows={state.totalLength}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={isRestricted ? [25, 50, 100, 150, 200, 250] : [50, 100, 150, 200, 250]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.loading}
          progressComponent={<Loader/>}
          persistTableHead
          responsive
        />
      </div>
    </Fragment>
  );
};

export default OPIContent;
